<template>
  <div class="company-picker">
    <v-autocomplete
      :value="value"
      :items="displayedItems"
      :loading="isSearching"
      :search-input.sync="searchText"
      :disabled="disabled"
      item-text="name"
      item-value="id"
      :label="fieldLabel"
      placeholder="Type to search companies…"
      prepend-inner-icon="mdi-magnify"
      outlined
      dense
      multiple
      chips
      small-chips
      deletable-chips
      clearable
      no-filter
      :attach="attach"
      :menu-props="menuProps"
      class="company-picker__autocomplete"
      data-test="company-picker"
      @input="onSelectionChange"
      @update:search-input="onSearchInput">
      <!-- Select / deselect every company in the current search result. -->
      <template v-if="items.length" v-slot:prepend-item>
        <v-list-item ripple class="company-picker__select-all" @mousedown.prevent @click="toggleAll">
          <v-list-item-action>
            <v-checkbox
              :input-value="allResultsSelected"
              :indeterminate="someResultsSelected && !allResultsSelected"
              dense
              hide-details
              readonly />
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title class="company-picker__item-name">
              {{ allResultsSelected ? 'Deselect all' : 'Select all' }}
              <span class="company-picker__muted">({{ items.length }}{{ searchText ? ' matching' : '' }})</span>
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action-text class="company-picker__count">
            {{ selectedCount }} selected
          </v-list-item-action-text>
        </v-list-item>
        <v-divider />
      </template>

      <template v-slot:no-data>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="company-picker__item-name">
              {{ noDataText }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <!-- Chip in the input. A suspended/expired company stays selectable — the server owns
           eligibility — so the badge is purely a warning. -->
      <template v-slot:selection="{ item, index, selected }">
        <v-chip
          v-if="index < visibleChipCount"
          small
          :input-value="selected"
          close
          class="company-picker__chip"
          @click:close="removeCompany(item)">
          <v-icon
            v-if="warningFor(item)"
            x-small
            left
            color="warning"
            class="company-picker__chip-badge">
            mdi-alert-outline
          </v-icon>
          <span class="company-picker__chip-text">{{ item.name || '(unnamed)' }}</span>
        </v-chip>
        <!-- Chips that don't fit on one line collapse into a single "+N" chip. -->
        <v-chip
          v-else-if="index === visibleChipCount"
          small
          class="company-picker__chip company-picker__chip--overflow"
          :title="hiddenCompanyNames">
          +{{ selectedCount - visibleChipCount }}
        </v-chip>
      </template>

      <template v-slot:item="{ item, on, attrs }">
        <v-list-item v-on="on" v-bind="attrs">
          <v-list-item-action>
            <v-checkbox :input-value="isSelected(item)" dense hide-details readonly />
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title class="company-picker__item-name">
              {{ item.name || '(unnamed)' }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="warningFor(item)" class="company-picker__item-warning">
              <v-icon x-small color="warning">mdi-alert-outline</v-icon>
              {{ warningFor(item) }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>

    <!-- Off-screen copies of the chips, measured to decide how many fit on one line. -->
    <div class="company-picker__measure" aria-hidden="true">
      <v-chip
        v-for="company in selectedCompanies"
        :key="company.id"
        ref="measureChip"
        small
        close
        class="company-picker__chip">
        <v-icon v-if="warningFor(company)" x-small left>mdi-alert-outline</v-icon>
        <span class="company-picker__chip-text">{{ company.name || '(unnamed)' }}</span>
      </v-chip>
      <v-chip ref="measureOverflow" small class="company-picker__chip">+{{ selectedCount }}</v-chip>
    </div>
  </div>
</template>

<script>
  import { warn } from '@/utils/debugLogger';

  const DEBOUNCE_MS = 400;
  const PAGE_SIZE = 25;

  /**
   * CompanyPicker — server-searched multi-select over
   * `GET api/Management/Releases/companies?search&page&itemsPerPage`.
   *
   * `no-filter` is set deliberately: the SERVER owns filtering, so v-autocomplete must never
   * hide an item our fetch deliberately returned.
   *
   * Race discipline: `SEARCH_COMPANIES` takes no abort signal (it is a plain Vuex action over
   * httpService), so instead of an AbortController we guard with a monotonic request sequence
   * number — a slow earlier response whose sequence is no longer the newest is discarded and can
   * never overwrite a fresher result set.
   *
   * The picker never reads `RELEASE_NOTES/GET_COMPANY_OPTIONS`: that state is global and would be
   * clobbered by any other consumer. It keeps the resolved page locally instead.
   */
  export default {
    name: 'CompanyPicker',

    props: {
      /** Selected company ids (v-model). */
      value: { type: Array, default: () => [] },
      disabled: { type: Boolean, default: false },
      /**
       * Forwarded to the inner v-autocomplete. Inside a `v-dialog` the detached menu can land
       * underneath the dialog overlay, so callers pass `attach` (an empty string means "attach
       * to my own parent node") to keep the menu inside the dialog's stacking context.
       */
      attach: { type: [Boolean, String, Object], default: false },
    },

    data() {
      return {
        searchText: '',
        // Latest page returned by the server.
        items: [],
        // Full DTOs for everything the user has ever selected, so a chip never goes blank when a
        // later search no longer returns that company.
        selectedCompanies: [],
        isSearching: false,
        // Debounce handle for keystroke-coalescing. Cleared in beforeDestroy.
        searchTimer: null,
        // Monotonic request id; only the newest response is allowed to write state.
        requestSeq: 0,
        // Set when the last search failed, so the menu can say so instead of looking empty.
        loadFailed: false,
        // How many chips fit on one line; the rest collapse into a "+N" chip.
        visibleChipCount: Infinity,
        resizeObserver: null,
      };
    },

    computed: {
      selectedCount() {
        return (this.value || []).length;
      },

      fieldLabel() {
        return this.selectedCount ? `Companies (${this.selectedCount} selected)` : 'Companies';
      },

      /** Names behind the "+N" chip, shown as its tooltip. */
      hiddenCompanyNames() {
        return this.selectedCompanies
          .slice(this.visibleChipCount)
          .map((company) => company.name || '(unnamed)')
          .join(', ');
      },

      allResultsSelected() {
        return this.items.length > 0 && this.items.every((company) => this.isSelected(company));
      },

      someResultsSelected() {
        return this.items.some((company) => this.isSelected(company));
      },

      menuProps() {
        return { offsetY: true, maxHeight: 320 };
      },

      /**
       * The menu is never silently empty: it says whether we are still loading, whether the
       * request failed, or whether the server genuinely returned nothing. `hide-no-data` used to
       * swallow all three cases into an invisible menu.
       */
      noDataText() {
        if (this.isSearching) return 'Loading companies…';
        if (this.loadFailed) return 'Could not load companies. Try again.';
        return this.searchText ? 'No companies match that search.' : 'No companies available.';
      },

      /**
       * Server page merged with every previously-selected company, deduped by id.
       * Selected entries come first so they stay reachable at the top of the menu.
       */
      displayedItems() {
        const seen = new Set();
        const merged = [];
        this.selectedCompanies.concat(this.items).forEach((company) => {
          if (!company || company.id == null) return;
          const key = String(company.id);
          if (seen.has(key)) return;
          seen.add(key);
          merged.push(company);
        });
        return merged;
      },
    },

    watch: {
      value: {
        immediate: true,
        handler() {
          this.syncSelectedCompanies();
          this.$nextTick(this.measureChips);
        },
      },
    },

    mounted() {
      // Prime page 1 so the menu is useful on first open rather than empty until a keystroke.
      this.fetchCompanies('');
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.measureChips());
        this.resizeObserver.observe(this.$el);
      }
      this.$nextTick(this.measureChips);
    },

    beforeDestroy() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.resizeObserver = null;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = null;
      // Bump the sequence so any response still in flight is discarded rather than
      // writing into a destroyed component.
      this.requestSeq += 1;
    },

    methods: {
      /**
       * Human-readable warning for a company that the server may refuse to email.
       * Every field is probed defensively — the companies DTO is unverified, and rendering
       * nothing is strictly better than rendering a wrong badge.
       * @param {object} company
       * @returns {string} warning label, or '' when the DTO says nothing about it
       */
      warningFor(company) {
        if (!company) return '';
        if (company.isSuspended === true || company.suspended === true) return 'Suspended';
        if (company.isExpired === true || company.expired === true) return 'Licence expired';
        if (typeof company.status === 'string') {
          const status = company.status.toLowerCase();
          if (status.includes('suspend')) return 'Suspended';
          if (status.includes('expire')) return 'Licence expired';
        }
        return '';
      },

      isSelected(company) {
        if (!company || company.id == null) return false;
        return (this.value || []).some((id) => String(id) === String(company.id));
      },

      onSearchInput() {
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.searchTimer = null;
          this.fetchCompanies(this.searchText || '');
        }, DEBOUNCE_MS);
      },

      onSelectionChange(ids) {
        const next = Array.isArray(ids) ? ids : [];
        this.$emit('input', next);
        // Resolve immediately from what we currently know so `update:companies` is not a tick
        // behind `input`; the `value` watcher will re-sync once the parent echoes the new ids.
        this.rememberSelected(next);
        this.$emit('update:companies', this.resolveCompanies(next));
      },

      /**
       * Selects every company in the current result page, or — when they are all already
       * selected — removes just those. Selections outside the current result are kept.
       */
      toggleAll() {
        const resultIds = this.items.map((company) => company.id);
        const current = this.value || [];
        if (this.allResultsSelected) {
          const resultKeys = new Set(resultIds.map(String));
          this.onSelectionChange(current.filter((id) => !resultKeys.has(String(id))));
          return;
        }
        const currentKeys = new Set(current.map(String));
        this.onSelectionChange(current.concat(resultIds.filter((id) => !currentKeys.has(String(id)))));
      },

      /**
       * Works out how many chips fit on a single line of the field, leaving room for the "+N"
       * chip and the search input. Widths come from the hidden measurement copies.
       */
      measureChips() {
        const container = this.$el && this.$el.querySelector('.v-select__selections');
        const chips = this.$refs.measureChip || [];
        if (!container || !chips.length) {
          this.visibleChipCount = Infinity;
          return;
        }
        const CHIP_GAP = 8; // v-chip margin inside .v-select__selections
        const INPUT_RESERVE = 64; // min room left for typing a search
        const available = container.clientWidth - INPUT_RESERVE;
        const widths = chips.map((chip) => (chip.$el || chip).offsetWidth + CHIP_GAP);

        const total = widths.reduce((sum, width) => sum + width, 0);
        if (total <= available) {
          this.visibleChipCount = Infinity;
          return;
        }

        const overflowWidth = (this.$refs.measureOverflow.$el || this.$refs.measureOverflow).offsetWidth + CHIP_GAP;
        let used = overflowWidth;
        let count = 0;
        while (count < widths.length && used + widths[count] <= available) {
          used += widths[count];
          count += 1;
        }
        this.visibleChipCount = count;
      },

      removeCompany(company) {
        if (!company || company.id == null) return;
        const next = (this.value || []).filter((id) => String(id) !== String(company.id));
        this.onSelectionChange(next);
      },

      /** Keeps `selectedCompanies` covering exactly the current ids (dropping stale entries). */
      syncSelectedCompanies() {
        this.rememberSelected(this.value || []);
        this.selectedCompanies = this.resolveCompanies(this.value || []);
      },

      /** Copies the DTO for each newly-selected id out of the current page into the cache. */
      rememberSelected(ids) {
        const pool = this.selectedCompanies.concat(this.items);
        (ids || []).forEach((id) => {
          const known = this.selectedCompanies.some((c) => String(c.id) === String(id));
          if (known) return;
          const found = pool.find((c) => c && String(c.id) === String(id));
          if (found) this.selectedCompanies.push(found);
        });
      },

      /**
       * Maps ids to DTOs. An id we have never seen a DTO for still yields a placeholder object
       * so v-autocomplete can render a chip instead of dropping the selection silently.
       */
      resolveCompanies(ids) {
        const pool = this.selectedCompanies.concat(this.items);
        return (ids || []).map((id) => {
          const found = pool.find((c) => c && String(c.id) === String(id));
          return found || { id, name: String(id) };
        });
      },

      /**
       * Coerces one server row into the `{ id, name }` shape `item-value` / `item-text` need.
       *
       * The companies DTO is unverified (plan addendum §A.5), so the row may name its key
       * `companyId` and its label `companyName` — or nest the whole company under `company`, the
       * way the access-list rows do. Assuming plain `id`/`name` made every row fail the
       * `id != null` guard below, which emptied the menu with no error anywhere.
       * The original row is spread through untouched so `warningFor` still sees its own fields.
       * @param {object} row
       * @returns {object|null} normalised company, or null when no id can be found
       */
      normalizeCompany(row) {
        if (!row) return null;
        const source = row.company && typeof row.company === 'object' ? row.company : row;

        const id = [row.id, row.companyId, source.id, source.companyId, row.value, row.key].find(
          (candidate) => candidate !== undefined && candidate !== null && candidate !== '',
        );
        if (id === undefined) return null;

        const name = [
          source.name,
          source.companyName,
          row.name,
          row.companyName,
          row.displayName,
          row.title,
          row.text,
        ].find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);

        return { ...source, ...row, id, name: name || String(id) };
      },

      /**
       * Runs one server search. Only the newest sequence writes to state, so a slow earlier
       * response cannot overwrite a newer one.
       * @param {string} search
       */
      async fetchCompanies(search) {
        this.requestSeq += 1;
        const seq = this.requestSeq;
        this.isSearching = true;

        try {
          const data = await this.$store.dispatch('RELEASE_NOTES/SEARCH_COMPANIES', {
            search: (search || '').trim() || null,
            page: 1,
            itemsPerPage: PAGE_SIZE,
          });
          if (seq !== this.requestSeq) return;
          // Unverified shape (addendum §A.5): tolerate a bare array, { items, total }, and the
          // `data` envelope some endpoints wrap their page in.
          let rows = data;
          if (rows && !Array.isArray(rows) && rows.data) rows = rows.data;
          if (rows && !Array.isArray(rows)) rows = rows.items || rows.results || [];
          if (!Array.isArray(rows)) rows = [];

          this.items = rows.map((row) => this.normalizeCompany(row)).filter(Boolean);
          // SEARCH_COMPANIES swallows its own errors ($handleError) and resolves undefined, so a
          // failed request is indistinguishable from an empty page here — treat both as "no data"
          // but flag an outright undefined result as a failure so the menu can say so.
          this.loadFailed = data === undefined;
        } catch (err) {
          if (seq !== this.requestSeq) return;
          this.loadFailed = true;
          warn('[CompanyPicker] company search failed', err);
        } finally {
          if (seq === this.requestSeq) this.isSearching = false;
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .company-picker {
    position: relative;
  }

  .company-picker__measure {
    position: absolute;
    top: 0;
    left: 0;
    height: 0;
    overflow: hidden;
    visibility: hidden;
    white-space: nowrap;
    pointer-events: none;
  }

  .company-picker__chip--overflow {
    font-weight: 600;
  }

  .company-picker__muted,
  .company-picker__count {
    opacity: 0.7;
    font-size: 0.75rem;
  }

  .company-picker__chip-text {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .company-picker__item-name {
    font-size: 0.8125rem;
  }

  .company-picker__item-warning {
    font-size: 0.6875rem;
    color: var(--v-warning-base);
  }
</style>
